import { ITravellerDetailsResponseData } from "@tm/api-utils";
import { getAnalytics } from "@tm/insurance/analytics";
import {
  convertDateToUnix,
  convertUnixToDate,
  getTravDetailsFromMemory,
  setTravDetailsInMemory,
} from "@tm/insurance/utils";
import {
  AddTravellerDetails,
  DesktopLayout,
  FlexComp,
  GreatChoice,
  ListingEditDetails,
  ListingTravelDetails,
  SelectCountryCodeModal,
} from "@tm/static";
import { usePlanType } from "@tm/ui";
import { ExperimentTrackingString, useBreakpointV2 } from "@tm/ui-widgets";
// import "pwa-mmt-ui-header/ssr/header.css";
import React, { useContext, useEffect, useState } from "react";
import { getMoreBenefits } from "./utils";

import {
  getSelectedCountriesFromLocalStorage,
  initializePageTracking,
  trackEvent,
} from "@tm/insurance/analytics/xdm";
import { pageStack, PAGE_NAME_CONSTANTS } from "@tm/insurance/navigation";
import { ICountryData } from "../../../components/AddTravellerDetail/type";
import PaymentFailedModal from "../../../components/PaymentFailedModal/PaymentFailedModal";
import { COUNTRY_DATA } from "./countryData";

type ITravelDetails = {
  countries?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  travellers?: string | null;
  purpose?: string | null;
};

export interface InsTravDetailProps {
  travellerDetailData: ITravellerDetailsResponseData;
  setActiveModal: (modal: string) => void;
  isLoading: boolean;
  checkoutError?: any;
  handleTravellerPayment: (formData: any) => Promise<void>;
  onFormDataChange?: (formData: any) => void;
  getSelectedCountry?: (selectedCountry: ICountryData | undefined) => void;
  firstErrorIndex: number;
  setFirstErrorIndex: (index: number) => void;
  onSetError?: any;
}

const analytics = getAnalytics();

const InsuranceTravellerDetail = ({
  travellerDetailData,
  setActiveModal,
  isLoading,
  checkoutError,
  handleTravellerPayment,
  onFormDataChange,
  getSelectedCountry,
  firstErrorIndex,
  setFirstErrorIndex,
  onSetError,
}: InsTravDetailProps) => {
  const device = useBreakpointV2();
  const { type } = usePlanType();

  const {
    title,
    travellerPlanInfo,
    planInfo,
    travellerDetail,
    tnc,
    previewBenefits,
    planBenefits,
    travelInfo,
    widgets,
    vendorDisplayIcon,
    isDomesticBooking,
    chatBotInfo,
  } = travellerDetailData || {};
  const travellerDataFromStore = getTravDetailsFromMemory();
  const { countryCode } = travellerDataFromStore || {};
  const { persuasionBanner } = widgets || {};

  const [travelDetails, setTravelDetails] = useState<ITravelDetails | null>(
    null
  );
  const [showBenefits, setShowBenefits] = useState<boolean>(false);
  const [showPaymentFailModal, setShowPaymentFailModal] = useState(false);
  const [showCodeModal, setCodeModal] = useState(false);
  const [countryData, setCountryData] = useState<ICountryData[]>(COUNTRY_DATA);
  const [addOnProductDetails, setAddOnProductDetails] = useState<any>();

  const { countries, startDate, endDate, travellers, purpose } =
    travelDetails || {};

  const experiment = useContext(ExperimentTrackingString) || "";

  useEffect(() => {
    pageStack.push(PAGE_NAME_CONSTANTS.TRAVELLER_DETAILS);
    const queryParams = new URLSearchParams(window.location.search);
    analytics?.trackPageLoadEvent({ pageName: "REVIEW", experiment }, type);

    const selectedCountries = getSelectedCountriesFromLocalStorage(
      queryParams.get("countries")?.split(",") || []
    );

    const segment = queryParams.get("segment");
    const startDate = queryParams.get("startDate");
    const endDate = queryParams.get("endDate");
    const travellers = queryParams.get("travellers");
    const subpageName = queryParams.get("type");
    const productId = queryParams.get("productId");

    let start, end;
    if (startDate && endDate) {
      start = convertUnixToDate(Number(startDate));
      end = convertUnixToDate(Number(endDate));
    }

    initializePageTracking(
      {
        page_context: {
          page_name: "review",
          page_type: "funnel",
          funnel_step: "review",
          ...(subpageName && { subpage_name: subpageName }),
        },
        event_detail: {
          ...(productId && {
            components: [
              {
                product_list: [{ id: productId ?? "" }],
              },
            ],
          }),
        },
      },
      {
        countries: selectedCountries,
        journeyType: segment || "",
        travellers: travellers ? parseInt(travellers) : 1,
        fromDateTime: start ? convertDateToUnix(start) : 0,
        toDateTime: end ? convertDateToUnix(end) : 0,
        isStoreSearchContext: true,
      }
    );
  }, []);

  useEffect(() => {
    let queryParams = new URLSearchParams(window.location.search);
    const countries =
      travellerDetailData.travelDetails &&
      travellerDetailData.travelDetails.countries;
    const startDate = queryParams.get("startDate");
    const endDate = queryParams.get("endDate");
    const travellers = queryParams.get("travellers");
    const purpose = queryParams.get("purpose");
    const paymentSuccess = queryParams.get("paymentSuccess");

    setTravelDetails({
      countries,
      startDate,
      endDate,
      travellers,
      purpose,
    });

    if (paymentSuccess === "false") {
      setShowPaymentFailModal(true);
    }
  }, []);

  useEffect(() => {
    const countries = countryData.map((country) => ({
      ...country,
      isSelected: country.countryCode === (countryCode || "+91") ? true : false,
    }));
    setCountryData(countries);
  }, []);

  const toggleCountryModal = () => setCodeModal(!showCodeModal);

  const handleCountryCodeModal = (countries: ICountryData[]) => {
    setCountryData(countries);
    toggleCountryModal();

    const selectedCountry = countries?.find((item) => item.isSelected);
    setTravDetailsInMemory({
      ...travellerDataFromStore,
      countryCode: selectedCountry?.countryCode,
    });
  };

  const handleViewBenefits = () => {
    setShowBenefits(!showBenefits);
  };

  const handleTncClick = () => {
    analytics?.trackTravDetailsEvents(
      {
        actionType: "TnC_clicked",
      },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "TnC_clicked",
      },
    });
  };

  return (
    <React.Fragment>
      <DesktopLayout
        pageName="ADD_TRAVELLERS"
        chatbotBottomMargin={100}
        height={isDomesticBooking ? "725px" : "620px"}
        title={device === "lg" ? title : isDomesticBooking ? {} : title}
        chatBotInfo={chatBotInfo}
        leftComp={
          <React.Fragment>
            <FlexComp flexDirection="column" gap="16px">
              {isDomesticBooking && device === "sm" && (
                <GreatChoice
                  icon={persuasionBanner?.icon}
                  title={persuasionBanner?.title}
                  info={persuasionBanner?.description}
                  background="#4A4A4A"
                  padding="8px 12px 8px 12px"
                  radius="8px"
                />
              )}
              <ListingEditDetails
                mode={travelInfo?.mode}
                title={travelInfo?.countries ?? countries}
                travelDates={{
                  startDate: convertUnixToDate(
                    Number(travelInfo?.travelDates?.startDate ?? startDate)
                  ),
                  endDate: convertUnixToDate(
                    Number(travelInfo?.travelDates?.endDate ?? endDate)
                  ),
                }}
                travelPurpose={travelInfo?.travelPurpose ?? purpose}
                travellerCount={Number(travelInfo?.travellers || travellers)}
                showEditIcon={false}
              />
              <ListingTravelDetails
                data={planInfo}
                previewBenefits={previewBenefits}
                tnc={tnc}
                handleTnc={handleTncClick}
                handleViewBenefits={handleViewBenefits}
                vendorDisplayIcon={vendorDisplayIcon}
                addOnProductDetails={addOnProductDetails}
              />
            </FlexComp>

            {device === "lg" && (
              <GreatChoice
                icon={
                  persuasionBanner
                    ? persuasionBanner?.icon
                    : travellerPlanInfo?.icon
                }
                title={
                  persuasionBanner
                    ? persuasionBanner?.title
                    : travellerPlanInfo?.title
                }
                info={
                  persuasionBanner
                    ? persuasionBanner?.description
                    : travellerPlanInfo?.info
                }
              />
            )}
          </React.Fragment>
        }
        rightComp={
          <React.Fragment>
            <AddTravellerDetails
              data={travellerDetailData}
              title={travellerDetail?.title}
              info={travellerDetail?.info}
              countryData={countryData}
              handleCountryCodeModal={toggleCountryModal}
              benefitsData={getMoreBenefits(planBenefits)}
              benefitsTitle={planBenefits?.heading}
              travellerCount={Number(travelInfo?.travellers || travellers)}
              showBenefits={showBenefits}
              handleViewBenefits={handleViewBenefits}
              addOnProductDetails={addOnProductDetails}
              setAddOnProductDetails={setAddOnProductDetails}
              setActiveModal={setActiveModal}
              isLoading={isLoading}
              checkoutError={checkoutError}
              handleTravellerPayment={handleTravellerPayment}
              onFormDataChange={onFormDataChange}
              getSelectedCountry={getSelectedCountry}
              firstErrorIndex={firstErrorIndex}
              setFirstErrorIndex={setFirstErrorIndex}
              onSetError={onSetError}
            />
          </React.Fragment>
        }
      ></DesktopLayout>

      {showCodeModal && (
        <SelectCountryCodeModal
          countryData={countryData}
          handleSelectCountry={handleCountryCodeModal}
          handleClose={toggleCountryModal}
        />
      )}

      {showPaymentFailModal && (
        <PaymentFailedModal
          title="Payment Failed"
          info="Oops, your payment could not be processed!"
          noteTitle="Note: "
          noteInfo="In case the amount has been deducted from your bank account, the same will be credited back within in 2-4 hours"
          retryBtnTxt="RETRY PAYMENT"
          backBtnTxt="GO BACK"
        />
      )}
    </React.Fragment>
  );
};

export default InsuranceTravellerDetail;
